import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircularProgress, Box, Typography } from '@mui/material';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../config/firebase';

/**
 * Establishes a Firebase session from a short-lived SSO custom token.
 *
 * Raw ID tokens are intentionally not accepted here: storing one does not
 * authenticate Firebase on this origin and caused the route guard to reject
 * otherwise successful portal launches.
 */
const SSOTokenHandler: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [isAuthenticating, setIsAuthenticating] = useState(
    () => typeof window !== 'undefined'
      && new URLSearchParams(window.location.search).has('customToken')
  );

  useEffect(() => {
    const handleSSOToken = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const customToken = urlParams.get('customToken');

      if (!customToken) return;

      setIsAuthenticating(true);

      const cleanUrl = () => {
        window.history.replaceState(
          {},
          document.title,
          `${window.location.pathname}${window.location.hash}`
        );
      };

      console.log('🔐 SSO custom token detected - signing in via Firebase...');
      try {
        const result = await signInWithCustomToken(auth, customToken);
        console.log('✅ Firebase SSO sign-in complete:', result.user.email);
        cleanUrl();
        navigate('/', { replace: true });
      } catch (error: any) {
        console.error('❌ signInWithCustomToken failed:', error);
        cleanUrl();
        alert(`SSO Error: ${error?.message || 'Failed to sign in via SSO.'}`);
        navigate('/login', { replace: true });
      } finally {
        setIsAuthenticating(false);
      }
    };

    handleSSOToken();
  }, [navigate]);

  if (isAuthenticating) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 2 }}>
        <CircularProgress size={60} />
        <Typography variant="h6">Authenticating via Single Sign-On...</Typography>
      </Box>
    );
  }

  return <>{children}</>;
};

export default SSOTokenHandler;
